import type {
  CatalogProduct,
  FollowUpTodoRequest,
  JobCreateLineItem,
  PricingProfileConfig,
  TaskDropdownDefinition,
  TaskListDefinition,
  TemplateTaskConfig,
} from '@techgitter/contracts';
import type {
  AdditionalDetailsValues,
  TicketTypeTemplate,
} from '../types/ticketTypeTemplate';
import {
  buildJobPayloadFromTemplate,
  type TemplateContextCustomer,
} from './buildJobPayloadFromTemplate';
import { interpolateTemplate } from './templateEngine';
import { standardBusinessHoursReference } from './timeWindowResolver';

/**
 * Template follow-up to-dos, resolved on the ticket form.
 *
 * A template's `followUpTodos` each name another template whose ticket is
 * raised on one of this ticket's dates plus an offset (first use: removing a
 * Conditional Access country exception the day after a trip ends). The form
 * resolves each into a `FollowUpTodoRequest` when the ticket is saved — the
 * follow-up ticket fully priced, the same way Recurring Tasks snapshots one —
 * and sm4rt-api stores it on a to-do that raises it on the day.
 *
 * The follow-up ticket's fields are the source ticket's values for any field
 * the two templates share by key (e.g. `countries`). Its line items are priced
 * against standard business hours on the scheduled date, so a ticket saved in
 * the evening doesn't freeze an after-hours rate onto a ticket raised at 7am.
 */

export interface ResolvedFollowUp {
  /** Index into the source template's `followUpTodos`. */
  index: number;
  /** The template the follow-up ticket is raised from. */
  target: TicketTypeTemplate;
  request: FollowUpTodoRequest;
}

export interface FollowUpProblem {
  index: number;
  /** `missing-date` is the normal state of a form not yet filled in;
   *  `missing-template` means the catalog is stale or misconfigured. */
  kind: 'missing-date' | 'missing-template';
  message: string;
}

export interface FollowUpResolution {
  followUps: ResolvedFollowUp[];
  /** Follow-ups that cannot be built. The ticket must not be saved while any
   *  exist — dropping one silently would mean the follow-up never happens. */
  problems: FollowUpProblem[];
}

export interface ResolveFollowUpsArgs {
  template: TicketTypeTemplate | null | undefined;
  /** The catalog the target templates are looked up in (per-customer merged). */
  templates: TicketTypeTemplate[];
  customer: TemplateContextCustomer;
  additionalDetails: AdditionalDetailsValues;
  products: CatalogProduct[];
  pricingProfilesConfig: PricingProfileConfig | null | undefined;
  taskConfigs?: TemplateTaskConfig[];
  taskListCatalog?: TaskListDefinition[];
  taskDropdowns?: TaskDropdownDefinition[];
  /** Per follow-up, per line: the tech's No charge choice, where it differs
   *  from the target template's default. Keyed by `noChargeKey`. */
  noChargeOverrides?: Record<string, boolean>;
  now?: Date;
}

/** `yyyy-MM-dd` + days, in calendar terms (no timezone arithmetic). */
export function addDaysIso(iso: string, days: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Key for one follow-up line's No charge choice. */
export function noChargeKey(followUpIndex: number, lineIndex: number): string {
  return `${followUpIndex}:${lineIndex}`;
}

/** The source ticket's values for the fields the target template also has. */
function carriedValues(
  target: TicketTypeTemplate,
  source: AdditionalDetailsValues,
): AdditionalDetailsValues {
  const carried: AdditionalDetailsValues = {};
  for (const field of target.additionalFields ?? []) {
    const value = source[field.fieldKey];
    if (value !== undefined && value !== null && value !== '') {
      carried[field.fieldKey] = value;
    }
  }
  return carried;
}

function applyNoCharge(
  lineItems: JobCreateLineItem[],
  index: number,
  overrides: Record<string, boolean>,
): JobCreateLineItem[] {
  return lineItems.map((item, lineIndex) => {
    const chosen = overrides[noChargeKey(index, lineIndex)];
    return chosen === undefined ? item : { ...item, nonChargeable: chosen };
  });
}

export function resolveFollowUps({
  template,
  templates,
  customer,
  additionalDetails,
  products,
  pricingProfilesConfig,
  taskConfigs = [],
  taskListCatalog,
  taskDropdowns,
  noChargeOverrides = {},
  now,
}: ResolveFollowUpsArgs): FollowUpResolution {
  const followUps: ResolvedFollowUp[] = [];
  const problems: FollowUpProblem[] = [];

  (template?.followUpTodos ?? []).forEach((spec, index) => {
    const target = templates.find((t) => t.ticketTypeId === spec.ticketTypeId);
    if (!target) {
      problems.push({
        index,
        kind: 'missing-template',
        message: `Follow-up template "${spec.ticketTypeId}" isn't available — refresh templates and try again`,
      });
      return;
    }

    const sourceDate = additionalDetails[spec.dateField];
    const scheduledDate =
      typeof sourceDate === 'string' ? addDaysIso(sourceDate, spec.offsetDays) : null;
    if (!scheduledDate) {
      const label =
        template?.additionalFields?.find((f) => f.fieldKey === spec.dateField)?.label ??
        spec.dateField;
      problems.push({
        index,
        kind: 'missing-date',
        message: `Enter ${label} to schedule "${target.displayName}"`,
      });
      return;
    }

    const ticketPayload = buildJobPayloadFromTemplate({
      template: target,
      customer,
      status: 'new',
      products,
      pricingProfilesConfig,
      // Re-stamped server-side to the day it is actually raised.
      ticketDate: scheduledDate,
      additionalDetails: carriedValues(target, additionalDetails),
      taskConfig: taskConfigs.find((c) => c.ticketTypeId === target.ticketTypeId) ?? null,
      taskListCatalog,
      taskDropdowns,
      pricingNow: standardBusinessHoursReference(new Date(`${scheduledDate}T09:00:00`)),
      now,
    });
    ticketPayload.lineItems = applyNoCharge(ticketPayload.lineItems, index, noChargeOverrides);

    const title = interpolateTemplate(spec.titleTemplate, additionalDetails).trim().slice(0, 200);
    followUps.push({
      index,
      target,
      request: {
        title: title || target.displayName,
        priority: spec.priority ?? 'medium',
        scheduledDate,
        ticketPayload,
      },
    });
  });

  return { followUps, problems };
}
